import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  type SimpleChanges,
} from '@angular/core';

import { HrAssistantComponent } from '@core/intelligence-smart-ui';
import { TellentAssistantComponent } from '@core/tellent-assistant-smart-ui';
import { injectDevFlagSignal } from '@core/tellent-dev-flags-logic';
import {
  provideAppShellThemeColors,
  TellentAppShellStateService,
  TellentAppShellThemeDirective,
  TellentAppShellThemeService,
  TellentMainNavigationStateService,
} from '@core/tellent-logic';
import { TellentDevFlag, TellentNavigationViewID } from '@core/tellent-types';
import { IconsContainerComponent } from '@core/user-interface-legacy';
import { WalkthroughStepId } from '@core/walkthrough-types';

import { TellentAppSwitcherNextComponent } from '../tellent-app-switcher-next/tellent-app-switcher-next.component';
import { TellentMainNavigationComponent } from '../tellent-navigation/main-navigation/main-navigation.component';
import { TellentSettingsNavigationComponent } from '../tellent-navigation/settings-navigation/settings-navigation.component';

@Component({
  selector: 'tlc-app-shell',
  styleUrls: [
    './tellent-app-shell.component.desktop.less',
    './tellent-app-shell.component.mobile.less',
    './tellent-app-shell.component.less',
  ],
  templateUrl: './tellent-app-shell.component.html',
  imports: [
    IconsContainerComponent,
    TellentMainNavigationComponent,
    TellentSettingsNavigationComponent,
    TellentAppSwitcherNextComponent,
    NgTemplateOutlet,
    HrAssistantComponent,
    TellentAssistantComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.device--desktop]': '!shellState.isMobile()',
    '[class.device--mobile]': 'shellState.isMobile()',
    '[class.layout--minimal]': 'shellState.isMinimalLayout()',
    '[class.view--settings]': 'shellState.view() === TellentNavigationID.Settings',
    '[class.view--main]': 'shellState.view() === TellentNavigationID.Main',
  },
  hostDirectives: [TellentAppShellThemeDirective],
  viewProviders: [TellentAppShellStateService],
  providers: [
    provideAppShellThemeColors({
      useFactory: () => inject(TellentAppShellThemeService).colors,
    }),
  ],
})
export class TellentAppShellComponent {
  protected readonly tellentAssistantFlag = injectDevFlagSignal(TellentDevFlag.TellentAssistant);

  public readonly TellentNavigationID = TellentNavigationViewID;

  public shellState = inject(TellentAppShellStateService);
  public mainNavState = inject(TellentMainNavigationStateService);


  public isMobile = input.required<boolean>();
  public isMinimalLayout = input(false);
  public shouldExpandNavOnHover = input(true);

  public appSwitcherTriggerOffset = signal<number>(0);

  public isSettingsView = computed(
    () => this.shellState.view() === TellentNavigationViewID.Settings,
  );

  public componentsWalkthroughId = input<
    Partial<{
      appSwitcher: WalkthroughStepId;
      navigation: WalkthroughStepId;
    }>
  >({});

  public isSidebarAvailable = computed(() => {
    const forceMaximized = this.mainNavState.forceMaximized();
    const isMobile = this.shellState.isMobile();
    const isMinimalLayout = this.shellState.isMinimalLayout();

    return !isMinimalLayout ? (isMobile && forceMaximized) || !isMobile : false;
  });

  constructor() {
    effect(() => {
      this.shellState.setIsMobileContext(this.isMobile());
      this.shellState.setIsMinimalLayout(this.isMinimalLayout());
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.isMobile) this.shellState.setIsMobileContext(this.isMobile());
    if (changes.isMinimalLayout) this.shellState.setIsMinimalLayout(this.isMinimalLayout());
  }
}
